import type {
  ActionLink,
  FeatureCard,
  MediaCard,
  NavItem,
  RouteMeta,
  SiteRoute,
} from "@/types/site";

export const routeMeta: Record<SiteRoute, RouteMeta> = {
  "/": {
    title: "Aletheia Development | Software, Teaching, Visual Work",
    description:
      "Aletheia Development is Kevin Kinnett's studio for software consulting, technical education, and visual experiments.",
  },
  "/engineering": {
    title: "Engineering & Consulting | Aletheia Development",
    description:
      "Software consulting for teams building or repairing .NET systems, cloud delivery, and technical foundations.",
  },
  "/academy": {
    title: "The Academy | Aletheia Development",
    description:
      "Technical teaching, tutorials, and workshops focused on software, editing, and explaining complex work clearly.",
  },
  "/creative-lab": {
    title: "Creative Lab | Aletheia Development",
    description:
      "A place for Gaussian splatting, digital capture, post-production, and visual experiments that sit close to technical work.",
  },
};

export const navItems: NavItem[] = [
  { label: "Home", description: "Studio overview", href: "/" },
  { label: "Engineering", description: "Consulting and systems", href: "/engineering" },
  { label: "Academy", description: "Learning and tutorials", href: "/academy" },
  { label: "Lab", description: "Visual work", href: "/creative-lab" },
];

export const footerLinks: ActionLink[] = [
  { label: "LinkedIn", state: "placeholder", note: "Destination to be supplied" },
  { label: "YouTube", state: "placeholder", note: "Destination to be supplied" },
  { label: "Contact", state: "placeholder", note: "Destination to be supplied" },
  { label: "kevinkinnett.com", href: "https://kevinkinnett.com", state: "active" },
];

export const homeHeroActions: ActionLink[] = [
  { label: "See the three areas", href: "#pillars", state: "active" },
  { label: "kevinkinnett.com", href: "https://kevinkinnett.com", state: "active" },
];

export const homePillars: FeatureCard[] = [
  {
    eyebrow: "Software consulting",
    title: "Engineering",
    description:
      "Hands-on software engineering, architecture, and delivery work for teams that need solid systems instead of presentation-deck language.",
    image: "/assets/home/image-02.webp",
    href: "/engineering",
    linkLabel: "See engineering work",
    accent: "primary",
  },
  {
    eyebrow: "Learning in public",
    title: "Academy",
    description:
      "Tutorials, breakdowns, and courses built around the things I actually use: .NET, web development, editing, and technical communication.",
    image: "/assets/home/image-03.webp",
    href: "/academy",
    linkLabel: "See the academy",
    accent: "tertiary",
  },
  {
    eyebrow: "Visual work",
    title: "Creative Lab",
    description:
      "A running archive of Gaussian splatting, post-production, and other experiments that sit somewhere between software and image-making.",
    image: "/assets/home/image-04.webp",
    href: "/creative-lab",
    linkLabel: "See the lab",
    accent: "secondary",
  },
];

export const socialCards: FeatureCard[] = [
  {
    eyebrow: "Teaching channel",
    title: "YouTube",
    description: "Longer explanations, walkthroughs, and experiments once the channel link is wired in.",
    image: "/assets/home/image-05.webp",
    accent: "tertiary",
  },
  {
    eyebrow: "Professional home",
    title: "LinkedIn",
    description: "A place for consulting updates, public writing, and the less permanent version of the work.",
    image: "/assets/home/screenshot.webp",
    accent: "primary",
  },
];

export const engineeringServiceCards: FeatureCard[] = [
  {
    eyebrow: "Core engineering",
    title: "Modern .NET systems",
    description:
      "Design and build .NET systems that are readable, testable, and built to handle change without collapsing into accidental complexity.",
    bullets: ["ASP.NET Core and Web API", "Service and system design", "Distributed systems"],
    accent: "primary",
  },
  {
    eyebrow: "Cloud and delivery",
    title: "Infrastructure that holds up",
    description:
      "CI/CD, cloud architecture, and operational setup that take real teams, real incidents, and real maintenance into account.",
    bullets: ["Azure and AWS", "CI/CD pipelines", "Infrastructure as code"],
    accent: "tertiary",
  },
  {
    eyebrow: "Technical direction",
    title: "Senior-level consulting",
    description:
      "Architecture review, modernization planning, and direct technical partnership for teams dealing with legacy systems or growth pressure.",
    bullets: ["Architecture audits", "Modernization strategy", "Technical leadership"],
    accent: "secondary",
  },
];

export const engineeringTracks: FeatureCard[] = [
  {
    eyebrow: "Clarity audit",
    title: "Figure out what is actually wrong",
    description:
      "A focused pass across architecture, delivery, and operational risk to identify the problems that matter instead of treating every smell as a crisis.",
    accent: "primary",
  },
  {
    eyebrow: "Build partner",
    title: "Ship the next thing carefully",
    description:
      "Work alongside the team on the parts that need strong judgment: system boundaries, delivery shape, and decisions that are painful to redo later.",
    accent: "tertiary",
  },
  {
    eyebrow: "Delivery coaching",
    title: "Help the team raise its bar",
    description:
      "Practical mentoring and technical working sessions for teams trying to make better architectural decisions more consistently.",
    accent: "secondary",
  },
];

export const academyHighlights: MediaCard[] = [
  {
    title: "Advanced .NET Architecture: Building for Longevity",
    category: "Featured tutorial",
    description:
      "A long-form walkthrough of service boundaries, integration seams, and the tradeoffs that tend to matter later, not just during the demo.",
    image: "/assets/academy/image-02.webp",
    meta: "42:15 | Published recently",
  },
  {
    title: "Color Grading for Software Demos",
    category: "Premiere Pro",
    description:
      "A practical look at how editing and color can make technical work easier to follow instead of just making it look expensive.",
    image: "/assets/academy/image-03.webp",
    meta: "12:04",
  },
  {
    title: "Functional C# with LINQ",
    category: "C# Core",
    description:
      "Composition patterns for business logic that stay readable after the original author has moved on.",
    image: "/assets/academy/image-04.webp",
    meta: "18:52",
  },
];

export const academyCourseCards: FeatureCard[] = [
  {
    eyebrow: "Course in progress",
    title: "Software systems that age well",
    description:
      "A practical path through boundaries, delivery, and maintainable service design for people building systems that need to last.",
    accent: "primary",
  },
  {
    eyebrow: "Workshop series",
    title: "Explaining technical work clearly",
    description:
      "How to present architecture, demos, and product ideas without sounding rehearsed, vague, or overproduced.",
    accent: "tertiary",
  },
  {
    eyebrow: "Mentorship lab",
    title: "Growing into technical leadership",
    description:
      "Coaching for engineers moving toward architecture, mentoring, and the kind of responsibility that is more than just having the senior title.",
    accent: "secondary",
  },
];

export const academySkills = [
  "C#",
  ".NET",
  "ASP.NET Core",
  "Web Development",
  "Azure",
  "Architecture",
  "Premiere Pro",
  "Technical Communication",
];

export const creativeFeatures: MediaCard[] = [
  {
    title: "Gaussian Splatting: Athenian Ruins",
    category: "Experimental 3D",
    description:
      "A spatial capture study focused on reconstruction, atmosphere, and what happens when technical capture starts to feel cinematic.",
    image: "/assets/creative-lab/image-02.webp",
  },
  {
    title: "Editorial Narrative: The Code",
    category: "Premiere Pro",
    description:
      "Post-production work shaped by technical subject matter rather than stock visual language.",
    image: "/assets/creative-lab/image-03.webp",
  },
];

export const creativeArchive: MediaCard[] = [
  {
    title: "Cybernetic Echoes",
    category: "Video design",
    description: "Machine-lit studies in texture, light, and atmosphere.",
    image: "/assets/creative-lab/image-04.webp",
    meta: "2024",
  },
  {
    title: "Flux Capacitance",
    category: "3D simulation",
    description: "Form, reflection, and motion tests with a metallic edge.",
    image: "/assets/creative-lab/image-05.webp",
    meta: "2023",
  },
  {
    title: "Analog Dreams",
    category: "Film editorial",
    description: "A study in projection, blur, dust, and old light.",
    image: "/assets/creative-lab/image-06.webp",
    meta: "2023",
  },
];

export const routeCtas: Record<Exclude<SiteRoute, "/">, ActionLink[]> = {
  "/engineering": [
    { label: "View detailed CV", href: "https://kevinkinnett.com", state: "active" },
    { label: "Contact", state: "placeholder", note: "Destination to be supplied" },
  ],
  "/academy": [
    { label: "View all on YouTube", state: "placeholder", note: "Destination to be supplied" },
    { label: "Join the early access list", state: "placeholder", note: "Destination to be supplied" },
  ],
  "/creative-lab": [
    { label: "Start a conversation", state: "placeholder", note: "Destination to be supplied" },
    { label: "View showreel", state: "placeholder", note: "Destination to be supplied" },
  ],
};

